import { Schema } from "effect"
import { AgentKind, Evidence, Principal, RankedRef, WorkRef } from "../common"
import { DocPath } from "../documents"
import {
  Assignee,
  Bet,
  CaptureSource,
  CloseResult,
  DoneCheck,
  EdgeKind,
  Hands,
  ItemPatch,
  Recurrence,
  Resolution,
  ReviewConfig,
} from "../domain/item"
import { AreaInterface, AreaPowers, Autonomy, Charter, Mind, PolicyDocument, TimedCap } from "../domain/org"
import { KpiRecipe, KpiSource, MetricCondition } from "../domain/plan"
import { Cadence, RepoBaseBranch, WorkerSettings } from "../domain/workspace"
import {
  AgentId,
  AreaId,
  ArtifactId,
  EdgeId,
  GoalId,
  Handle,
  IntegrationId,
  IntentKey,
  ItemId,
  KpiId,
  Markdown,
  Millis,
  PolicyId,
  ProfileId,
  Rank,
  RunnerId,
  Sha256,
  Slug,
  TypeKey,
  UserId,
  WorkRequestId,
} from "../ids"
import { Placement, PlacementDefaults } from "../placement"
import { ItemRouting, RoutingChoice, RoutingPolicy } from "../routing"

// #region graph-commands
const opt = Schema.optionalKey

export const GoalPatch = Schema.Struct({
  title: opt(Schema.String),
  why: opt(Schema.String),
  body: opt(Markdown),
  deadline: opt(Schema.NullOr(Millis)),
  kpiIds: opt(Schema.Array(KpiId)),
  tags: opt(Schema.Array(Slug)),
  /** What a person checks when judging the goal. */
  criteria: opt(Schema.Array(Schema.String)),
})
export const KpiPatch = Schema.Struct({
  name: opt(Schema.String),
  unit: opt(Schema.String),
  role: opt(Schema.Literals(["input", "lagging", "health"])),
  source: opt(KpiSource),
  latencyDays: opt(Schema.Int),
  minReportable: opt(Schema.NullOr(Schema.Int)),
  rollsUpTo: opt(Schema.Array(KpiId)),
  baseline: opt(Schema.NullOr(Schema.Struct({ value: Schema.Finite, at: Millis }))),
  /** Decision 0097: how the number is fetched (null removes it). A recipe-only patch is the lead's or the area agent's. */
  recipe: opt(Schema.NullOr(KpiRecipe)),
})
/**
 * Decision 0154 (review 10-23 #4): the item waits for this person's go before any worker starts it (go-live, spending,
 * anything the mandate reserves for a person). Decide opens a gate card to them that `blocks` the item (decision 0041:
 * only "Go ahead" releases it; another answer pauses it as theirs). A command key only: the record is that card.
 */
export const GoFrom = Schema.Struct({
  userId: UserId,
  /** What needs their go, in their terms ("spends real X credit"). Blank: the item's `why`. */
  why: opt(Schema.String.check(Schema.isMaxLength(600))),
})
export type GoFrom = typeof GoFrom.Type

/** WIP of an area or the project: at most `maxNow` top-level plannable items in Now. */
export const Wip = Schema.Struct({ maxNow: Schema.Int })
export const AreaPatch = Schema.Struct({
  name: opt(Schema.String),
  color: opt(Schema.String),
  /** The area's agent: owns the area's new items by default and accepts requests from other areas. */
  intakeAgentId: opt(Schema.NullOr(AgentId)),
  placement: opt(PlacementDefaults),
  powers: opt(AreaPowers),
  wip: opt(Wip),
  interfaces: opt(
    Schema.Struct({
      publishes: Schema.Array(AreaInterface),
      consumes: Schema.Array(Schema.Struct({ areaId: AreaId, key: Slug })),
    }),
  ),
})
export const AgentPatch = Schema.Struct({
  name: opt(Schema.String),
  title: opt(Schema.String),
  avatar: opt(Schema.Struct({ emoji: Schema.NullOr(Schema.String), color: Schema.String })),
  hands: opt(Hands),
  wake: opt(Schema.Struct({ debounceMs: Schema.Int })),
  areaId: opt(Schema.NullOr(AreaId)),
  /** Settings wave: unique among the project's agents and members. */
  handle: opt(Handle),
  /** Written only by TakeOverAgent (the AgentUpdated event shares this schema); UpdateAgent with it → Invalid. */
  ownerId: opt(UserId),
  /** Decision 0048: how far the agent goes in a turn that changes started. */
  autonomy: opt(Autonomy),
  /** Wave C (decision 0071): timed turns per day; keeper or admin (like every field here but the charter). */
  timedCap: opt(TimedCap),
  chainCap: opt(TimedCap),
})

/**
 * Graph commands: the ONLY payloads allowed inside a changeset or a plan_change proposal. Every plan edit — even
 * one drag in the UI — is a changeset, so plan versions, diffs, undo and revert have one uniform unit.
 * Ids are minted by the issuer. Defaults for omitted optional fields are applied in `decide` and written into
 * the event, so `evolve` never invents values.
 */
export const GraphCommand = Schema.TaggedUnion({
  // goals & KPIs
  CreateGoal: {
    goalId: GoalId,
    title: Schema.String,
    why: Schema.String, // not blank: decide rejects it with Invalid (planner rule)
    body: opt(Markdown),
    /** null or omitted = a person judges it (with `criteria`). */
    target: opt(Schema.NullOr(MetricCondition)),
    criteria: opt(Schema.Array(Schema.String)),
    deadline: opt(Millis),
    kpiIds: opt(Schema.Array(KpiId)),
    rank: opt(Rank),
  },
  UpdateGoal: { goalId: GoalId, patch: GoalPatch },
  SetGoalStatus: {
    goalId: GoalId,
    status: Schema.Literals(["active", "achieved", "missed", "abandoned"]),
    note: Schema.String,
  },
  /** change_kpi_target: targets live on goals (a judged goal may get one) and bets. */
  ChangeTarget: {
    subject: Schema.TaggedUnion({ goal: { id: GoalId }, item: { id: ItemId } }),
    condition: MetricCondition,
  },
  DefineKpi: {
    kpiId: KpiId,
    areaId: opt(AreaId),
    key: Slug,
    name: Schema.String,
    unit: Schema.String,
    direction: Schema.Literals(["up", "down"]),
    role: Schema.Literals(["input", "lagging", "health"]),
    /** Absent = manual (decision 0097: a KPI from an outside source is manual with a recipe). */
    source: opt(KpiSource),
    cadence: Schema.Literals(["hourly", "daily", "weekly"]),
    latencyDays: opt(Schema.Int),
    minReportable: opt(Schema.Int),
    rollsUpTo: opt(Schema.Array(KpiId)),
    /** Decision 0097: how a worker fetches the number. */
    recipe: opt(Schema.NullOr(KpiRecipe)),
  },
  UpdateKpi: { kpiId: KpiId, patch: KpiPatch },
  // areas & agents
  CreateArea: {
    areaId: AreaId,
    slug: Slug,
    name: Schema.String,
    color: Schema.String,
    charter: Charter,
    policyId: opt(PolicyId),
    placement: opt(PlacementDefaults),
    powers: opt(AreaPowers),
    wip: opt(Wip),
    templateId: opt(Schema.String),
  },
  UpdateArea: { areaId: AreaId, patch: AreaPatch },
  ReviseAreaCharter: { areaId: AreaId, charter: Charter },
  ArchiveArea: { areaId: AreaId },
  /**
   * revise_brief (decision 0049): the only writer of Project.description after genesis. `body` is the whole new brief
   * (≤ BRIEF_MAX_CHARS); `baseHash` is core `briefHash` of the text it was written against (stale → Invalid). A
   * person's applies at once (they manage the project); an agent's is always a proposal (rule `brief`).
   */
  ReviseBrief: { body: Markdown, baseHash: Sha256, why: opt(Schema.String) },
  /** hire_agent. `policyPreset` selects a default policy document for the role; the policy itself is a human-edited record. */
  HireAgent: {
    agentId: AgentId,
    role: Schema.Literals(["lead", "role"]),
    areaId: Schema.NullOr(AreaId),
    name: Schema.String,
    handle: Handle,
    title: Schema.String,
    charter: Charter,
    mind: Mind,
    hands: opt(Hands),
    policyId: PolicyId,
    policyPreset: Schema.Literals(["lead", "role", "reviewer"]),
  },
  UpdateAgent: { agentId: AgentId, patch: AgentPatch },
  ReviseAgentCharter: { agentId: AgentId, charter: Charter },
  ChangeAgentMind: { agentId: AgentId, mind: Mind },
  SetAgentStatus: { agentId: AgentId, status: Schema.Literals(["active", "paused", "retired"]) },
  /** People only (settings wave K3b): the actor becomes the agent's owner; `mind` replaces the mind in the same step. */
  TakeOverAgent: { agentId: AgentId, mind: opt(Mind) },
  // items (decision 0035)
  /**
   * A new item: planned (status backlog or todo) or captured (status triage, with a `source`). Omitted values get
   * decide's defaults, written into ItemCreated: type `task`; the parent's area; owner = the nearest agent above (or
   * the area's agent, or the lead), never the acting agent; assignee: none for plannable types, the person for a
   * person owner, a new worker for an agent owner; checks = the type's defaults; status `todo`.
   */
  CreateItem: {
    itemId: ItemId,
    type: opt(TypeKey),
    title: Schema.String,
    why: Schema.String, // not blank: decide rejects it with Invalid (planner rule)
    body: opt(Markdown),
    parentId: opt(Schema.NullOr(ItemId)),
    /** Omitted = the parent's area. Another area than the parent's = a cross-area request (request: pending). */
    areaId: opt(AreaId),
    /** Top-level items only. */
    goalIds: opt(Schema.Array(GoalId)),
    owner: opt(Schema.NullOr(Principal)),
    assignee: opt(Schema.NullOr(Assignee)),
    status: opt(Schema.Literals(["triage", "backlog", "todo"])),
    kind: opt(Schema.Literals(["work", "publish"])),
    checks: opt(Schema.Array(DoneCheck)),
    review: opt(ReviewConfig),
    dueAt: opt(Millis),
    notBefore: opt(Millis),
    recurrence: opt(Recurrence),
    bet: opt(Bet),
    placement: opt(Placement),
    hands: opt(Hands),
    routing: opt(Schema.NullOr(ItemRouting)),
    /** Decision 0101: the item changes no repo (research, data, analysis). */
    readOnly: opt(Schema.Boolean),
    tags: opt(Schema.Array(Slug)),
    paths: opt(Schema.Array(Schema.String)),
    integrations: opt(Schema.Array(IntegrationId)),
    supersedes: opt(ItemId),
    intentKey: IntentKey,
    rank: opt(Rank),
    /** Captures only (status triage): where it came from. */
    source: opt(CaptureSource),
    workRequest: opt(
      Schema.Struct({ requestId: WorkRequestId, revision: Schema.Int.check(Schema.isGreaterThan(0)) }),
    ),
    /** Decision 0154: hold the new item for a person's go (a gate card to them that blocks it). */
    goFrom: opt(GoFrom),
  },
  UpdateItem: { itemId: ItemId, patch: ItemPatch },
  /**
   * Re-parent and/or a planning status (at least one). A top-level item moving into or out of Now (`in_progress`) is
   * `start_item` and WIP-checked; never a worker leaf ("a worker starts it when it is ready").
   */
  MoveItem: {
    itemId: ItemId,
    parentId: opt(Schema.NullOr(ItemId)),
    status: opt(Schema.Literals(["triage", "backlog", "todo", "in_progress"])),
  },
  /** Owner and/or assignee (at least one). An agent's change to a person's item becomes a proposal to them (G7). */
  AssignItem: {
    itemId: ItemId,
    owner: opt(Schema.NullOr(Principal)),
    assignee: opt(Schema.NullOr(Assignee)),
  },
  /** After the checks froze (first attempt started) this needs a person: for agents it is always `propose`. */
  ReviseChecks: { itemId: ItemId, checks: Schema.Array(DoneCheck), review: opt(ReviewConfig) },
  /**
   * Close an item. `done`: through its later review stages (invariant 6); evidence is required when the item has
   * command or artifact checks (Mark done otherwise); a container needs every child closed. `cancelled`: with a
   * resolution (default wont_do); cascades to open descendants.
   */
  CloseItem: {
    itemId: ItemId,
    as: Schema.Literals(["done", "cancelled"]),
    note: Schema.String,
    evidence: opt(Schema.Array(Evidence)),
    resolution: opt(Resolution),
    /** Decision 0101: `done` only: what it delivered (absent = verified). */
    result: opt(CloseResult),
  },
  /** Run now (D4): one occurrence of a recurring item, as its child. Nothing creates occurrences on a schedule. */
  RunOccurrence: { itemId: ItemId, occurrenceId: ItemId },
  /** The receiving area's agent accepts or declines a cross-area request. */
  DecideRequest: { itemId: ItemId, accept: Schema.Boolean, note: opt(Schema.String) },
  Reprioritize: { ref: RankedRef, rank: Rank },
  // edges
  AddEdge: {
    edgeId: EdgeId,
    kind: EdgeKind,
    from: WorkRef,
    to: WorkRef,
    interfaceKey: opt(Slug),
    note: opt(Schema.String),
  },
  RemoveEdge: { edgeId: EdgeId },
  /**
   * A pending v1 plan change with no 1:1 v2 command (decision 0036, D10): decide always refuses it ("proposed before
   * the work-model update: propose it again"). Produced only by the upcaster.
   */
  LegacyChange: { v1: Schema.Json, summary: Schema.String },
})
export type GraphCommand = typeof GraphCommand.Type

/** Commands a chosen question option may run (re-decided at answer time, as the deciding human). */
export const FlowCommand = Schema.TaggedUnion({
  /**
   * New attempt after a block. Decision 0095: `routing` replaces the item's worker route (its validator route when the
   * validator is retried) in the same step: where the next attempt runs, and what it avoids.
   */
  RetryItem: { itemId: ItemId, note: opt(Schema.String), routing: opt(RoutingChoice) },
  UnblockItem: { itemId: ItemId, note: Schema.String, hold: opt(Schema.Boolean) }, // hold: also pause it (no new attempt)
  AcceptImpossible: { itemId: ItemId, note: Schema.String }, // → cancelled with resolution impossible{reason: note}
  PauseItem: { itemId: ItemId, paused: Schema.Boolean, note: opt(Schema.String) },
})
export type FlowCommand = typeof FlowCommand.Type
/**
 * Decision 0050: a person's pick on a human review card sets artifact states as the person who answered.
 * Option effects only (never a command of its own: `SetArtifactState` the command stays in ProjectCommand).
 */
export const ArtifactEffect = Schema.TaggedUnion({
  SetArtifactState: { artifactId: ArtifactId, state: Schema.Literals(["final", "rejected"]) },
  /**
   * Decision 0084 (§2.3): adopt a `proposals/<path>` draft (a `propose_page` card's accept) as the next version of the
   * page at `path`, re-decided as the person who answers. Option effect only.
   */
  AdoptDocument: { artifactId: ArtifactId, path: DocPath },
  /**
   * Decision 0086 (§4.2): adopt a `proposals/workspace/<path>` draft as the next version of the workspace page at
   * `path` (`workspace/people/<h>.md` or `workspace/guidelines/<t>.md`), re-decided as the person who answers. It
   * changes no project state: the project's outbox delivers it to the WorkspaceDO as that person. `personFor` is the
   * person page's user (only they may accept it); null for a guideline (owner or admin). Option effect only.
   */
  AdoptWorkspaceDocument: { artifactId: ArtifactId, path: DocPath, personFor: Schema.NullOr(UserId) },
})
export type ArtifactEffect = typeof ArtifactEffect.Type

/**
 * Decision 0119: the project settings a lead may propose on a card (`propose_settings`). Only these keys: never the
 * allowlist, repos, status, policies or members. Each value is saved whole, as in Settings.
 */
export const ProjectSettingsPatch = Schema.Struct({
  routing: opt(RoutingPolicy),
  /** Presets, attemptCap and noProgressCap. */
  workers: opt(WorkerSettings),
  wip: opt(Wip),
  /** Time zone and the daily check's hour. */
  cadence: opt(Cadence),
  baseBranches: opt(Schema.Array(RepoBaseBranch)),
})
export type ProjectSettingsPatch = typeof ProjectSettingsPatch.Type

/** The current values of the keys a settings card changes (null = not set; the card's diff and its stale check). */
export const ProjectSettingsBefore = Schema.Struct({
  routing: opt(Schema.NullOr(RoutingPolicy)),
  workers: opt(Schema.NullOr(WorkerSettings)),
  wip: opt(Wip),
  cadence: opt(Cadence),
  baseBranches: opt(Schema.NullOr(Schema.Array(RepoBaseBranch))),
})
export type ProjectSettingsBefore = typeof ProjectSettingsBefore.Type

/**
 * Decision 0119: a project settings change on a card. Option effect only: applied as the person who answers (an
 * UpdateProject, and an UpdateArea per area WIP), so the project's owner or a workspace admin decides it. Refused when
 * a value changed since the card opened (`before`).
 */
export const ProjectSettingsEffect = Schema.TaggedUnion({
  ChangeProjectSettings: {
    patch: ProjectSettingsPatch,
    /** Areas' WIP (Now items), each with the value the card was proposed against. */
    areaWip: opt(Schema.Array(Schema.Struct({ areaId: AreaId, maxNow: Schema.Int, before: Schema.Int }))),
    before: ProjectSettingsBefore,
  },
})
export type ProjectSettingsEffect = typeof ProjectSettingsEffect.Type

/**
 * Decision 0128 addendum (2026-10-20): a teammate's turn waits on someone's personal CLI login or subscription. The
 * login's owner gets a card (opened by Minga, never by an agent or a person) whose Share option carries this effect. It
 * changes no project state: core checks that the person who picks it owns the login, and the project's outbox sets the
 * profile's scope to Team as that person (`WorkspaceDO.setProfileScope` for a machine's login,
 * `PlatformRunnerDO.setSubscriptionScope` for a Platform Runner subscription), which re-check the owner. Option effect
 * only.
 */
export const LoginShareEffect = Schema.TaggedUnion({
  ShareLogin: {
    runnerId: RunnerId,
    profileId: ProfileId,
    /** The login's owner: the only person who may pick it. */
    ownerId: UserId,
    /** The CLI ("claude", "codex"), for the card's words. */
    agent: opt(AgentKind),
    /**
     * Decision 0166 §3.2: "Share in <project>": the outbox adds the card's project to the login's projects instead of
     * making it Team. Absent (cards from before 0166) = Team.
     */
    inProject: opt(Schema.Boolean),
  },
})
export type LoginShareEffect = typeof LoginShareEffect.Type

/**
 * Decision 0138 (lessons 1–4 §3, "grants that stick"): a policy change on a card, the way 0119 carries settings. A
 * person delegated in chat ("you may merge to develop", "don't make me review PRs"); the lead files one card in that
 * turn whose Apply option carries the policy's next document. Option effect only: applied as the person who answers
 * (`RevisePolicy`, so a project admin decides it; no agent does), refused when the policy changed since the card opened
 * (`revision` is the one it was proposed against).
 */
export const PolicyEffect = Schema.TaggedUnion({
  RevisePolicy: {
    policyId: PolicyId,
    /** The policy's revision the card was proposed against. */
    revision: Schema.Int,
    document: PolicyDocument,
  },
})
export type PolicyEffect = typeof PolicyEffect.Type

export const OptionEffect = Schema.Union([
  GraphCommand,
  FlowCommand,
  ArtifactEffect,
  ProjectSettingsEffect,
  LoginShareEffect,
  PolicyEffect,
])
export type OptionEffect = typeof OptionEffect.Type
// #endregion graph-commands
